import React from 'react';
import { Button, CircularProgress, Dialog, Icon, IconButton, Typography } from '@material-ui/core';
import { QRCodeSVG } from 'qrcode.react';
import styled from 'styled-components';
import { User } from 'types/user';
import { useQrSignIn } from './useQrSignIn';

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 32px 32px 28px;
  text-align: center;
`;

const CloseButton = styled(IconButton)`
  && {
    position: absolute;
    top: 4px;
    right: 4px;
  }
`;

const Title = styled(Typography)`
  && {
    font-size: 20px;
    font-weight: 700;
    color: #000;
    margin-bottom: 8px;
  }
`;

const Instruction = styled(Typography)`
  && {
    font-size: 14px;
    color: #767676;
    max-width: 320px;
    margin-bottom: 24px;
  }
`;

const CodeArea = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 288px;
  height: 288px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 16px;

  @media (max-width: 576px) {
    width: 100%;
    max-width: 288px;
    height: auto;
    aspect-ratio: 1;
  }
`;

const ErrorText = styled(Typography)`
  && {
    font-size: 14px;
    color: #767676;
    margin-bottom: 12px;
  }
`;

const QR_SIZE = 256;

type Props = {
  open: boolean;
  onClose: () => void;
  onSignedIn: (payload: { user: User; token: string }) => void;
};

export const QrSignInDialog = ({ open, onClose, onSignedIn }: Props) => {
  const { phase, url, retry } = useQrSignIn({ enabled: open, onReady: onSignedIn });

  return (
    <Dialog open={open} onClose={onClose} aria-labelledby="qr-sign-in-title" maxWidth="xs">
      <Content>
        <CloseButton onClick={onClose} aria-label="Close">
          <Icon>close</Icon>
        </CloseButton>
        <Title id="qr-sign-in-title" component="h2">
          Sign in with your phone
        </Title>
        <Instruction>Open the AutoAxess app on your phone and scan this code to sign in here.</Instruction>
        <CodeArea aria-live="polite">
          {phase === 'showing' && url && (
            <QRCodeSVG
              value={url}
              size={QR_SIZE}
              includeMargin={false}
              role="img"
              aria-label="QR code for signing in with the AutoAxess mobile app"
            />
          )}
          {phase === 'loading' && <CircularProgress aria-label="Preparing your sign-in code" />}
          {phase === 'error' && (
            <div>
              <ErrorText>We couldn't get a sign-in code.</ErrorText>
              <Button onClick={retry} color="primary" variant="outlined">
                Try again
              </Button>
            </div>
          )}
        </CodeArea>
      </Content>
    </Dialog>
  );
};
